import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LayoutGrid } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/lib/apiBase";

// Hotspot positions match `CourtLayoutDialog` so the picker visual is
// identical to the rest of the site. We keep them here (rather than a
// shared module) because the mapping is small and very unlikely to drift.
// Court 5 is shown on the facility image but is NOT a hotspot — it is
// covered by a "Soon to Open" overlay below.
const HOTSPOTS = [
  { name: "Court 4", top: "10%", left: "40%", width: "44%", height: "15%" },
  { name: "Court 2", top: "40%", left: "39%", width: "23%", height: "31%" },
  { name: "Court 3", top: "40%", left: "64%", width: "23%", height: "31%" },
  { name: "Court 1", top: "78%", left: "40%", width: "44%", height: "13%" },
];

const COURT5_HOTSPOT = { top: "10%", left: "13%", width: "23%", height: "42%" };

const FACILITY_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_court-reserve-54/artifacts/daewz7cf_kawit%20pickleball%20court_website.png";

/**
 * Modal court picker — opened from the "Switch Court" button next to the
 * "Currently Booking" badge on the booking form. Shows the facility layout
 * with every court clickable; selecting a court (other than the current
 * one) closes the dialog and routes to that court's booking page.
 */
const SwitchCourtDialog = ({ open, onOpenChange, currentCourtId }) => {
  const navigate = useNavigate();
  const [courts, setCourts] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${API}/courts`)
      .then((r) => {
        if (!cancelled) setCourts(r.data || []);
      })
      .catch(() => {
        // Non-fatal — hotspots silently no-op when courts are missing.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handlePick = (courtName) => {
    const target = courts.find((c) => c.name === courtName);
    if (!target) return;
    if (target.id === currentCourtId) {
      // Same court — just close, no navigation.
      onOpenChange?.(false);
      return;
    }
    onOpenChange?.(false);
    // Slight delay so the dialog animates out before the route swap.
    setTimeout(() => navigate(`/booking/${target.id}`), 60);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="switch-court-dialog"
        className="max-w-sm bg-[#0a0a0a] text-white"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-black tracking-tight flex items-center gap-2">
            <LayoutGrid size={18} className="text-[#8cc63f]" />
            Switch Court
          </DialogTitle>
        </DialogHeader>

        <div
          className="relative mx-auto w-full"
          style={{ maxWidth: "320px" }}
        >
          <img
            src={FACILITY_IMG}
            alt="Assenda Facility Layout"
            className="w-full h-auto rounded-md block"
          />
          {/* Court 5 "Soon to Open" overlay — not clickable. */}
          <div
            data-testid="switch-court-court5-soon"
            className="absolute flex items-center justify-center rounded-sm bg-black/70 backdrop-blur-[2px] border border-amber-500/60 text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-center px-1 leading-tight pointer-events-none"
            style={{
              top: COURT5_HOTSPOT.top,
              left: COURT5_HOTSPOT.left,
              width: COURT5_HOTSPOT.width,
              height: COURT5_HOTSPOT.height,
            }}
          >
            Soon to Open
          </div>
          {HOTSPOTS.map((hot) => {
            const c = courts.find((x) => x.name === hot.name);
            const isCurrent = c && c.id === currentCourtId;
            return (
              <button
                key={hot.name}
                type="button"
                data-testid={`switch-court-hotspot-${hot.name
                  .toLowerCase()
                  .replace(" ", "-")}`}
                onClick={() => handlePick(hot.name)}
                aria-label={
                  isCurrent
                    ? `${hot.name} (current — already booking here)`
                    : `Switch to ${hot.name}`
                }
                title={
                  isCurrent
                    ? `${hot.name} (current)`
                    : `Switch to ${hot.name}`
                }
                className={`absolute rounded-sm border-2 transition-all ${
                  isCurrent
                    ? "border-[#8cc63f] bg-[#8cc63f]/40 cursor-default"
                    : "border-transparent hover:border-[#8cc63f] hover:bg-[#8cc63f]/25 cursor-pointer"
                }`}
                style={{
                  top: hot.top,
                  left: hot.left,
                  width: hot.width,
                  height: hot.height,
                }}
              />
            );
          })}
        </div>

        <p className="text-xs text-[#a1a1aa] text-center mt-3">
          Tap a court to switch · Highlighted in{" "}
          <span className="text-[#8cc63f] font-semibold">green</span> is the
          current court
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default SwitchCourtDialog;
